import React, { useState, useEffect, forwardRef, useRef } from "react";
import { Link } from "react-router-dom";

import fixtureService from "../../services/FixtureService";
import calendarService from "../../services/CalendarService";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import Uploader from "../../components/Uploader";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { ApplicationRoles } from "../api-authorization/ApiAuthorizationConstants";
import ConfirmVideoUploadModal from "./ConfirmVideoUploadModal";
import { toast } from "react-toastify";

const FixtureDetailsModal = forwardRef((props, ref) => {
    const {show, onHide, userRole, fixture, onStartRoom} = props;
    const fixtureId = fixture?.fixtureId;
    const fixtureBureauId = fixture?.bureauMatchId;
    const fixtureRoomId = fixture?.fixtureRoomId;
    
    const typaheadRef = React.createRef();
    const uploaderRef = useRef(null);

    const [fixtureDetails, setFixtureDetails] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignUsersList, setAssignUsersList] = React.useState([]);
    const [showConfirmUpload, setShowConfirmUpload] = useState(false);

    useEffect(() => {
        if (!show) return;

        async function loadAssignUsers() {
            const users = await calendarService.getAssignUsersList();
            setAssignUsersList(users);
        }

        loadAssignUsers();
    }, [show]);

    useEffect(() => {
        if (!show) return;

        const needsRefresh =
            fixtureDetails?.fixtureRoomId !== fixtureRoomId ||
            fixtureDetails?.id !== fixtureId;

        if (!needsRefresh) return;

        async function loadFixtureDetails() {
            const details = await calendarService.getFixtureDetails(
                fixtureId ?? fixtureBureauId,
                fixtureRoomId
            );
            setFixtureDetails(details);
        }

        loadFixtureDetails();
    }, [fixtureId, fixtureRoomId, show]);

    const closeFixtureDetails = async () => {
        await onHide();
        setFixtureDetails(null);
        setSelectedUser(null);
    };

    if (fixtureDetails) {
        const canEdit = userRole === ApplicationRoles.Manager;
        const currentFixtureId = fixtureDetails.id ?? fixtureBureauId;
        const currentFixtureRoomId = fixtureDetails.fixtureRoomId;

        const existingUsers = fixtureDetails.assigned.map((x) => {
            return x.userId;
        });

        let users = canEdit
            ? assignUsersList
                ?.filter((x) => !existingUsers.includes(x.userId))
                .map((x) => {
                    return {id: x.userId, label: x.name};
                })
            : null;

        let videoUploadControls = (
            <Row>
                <Col>
                    <Uploader
                        ref={uploaderRef}
                        onUploadComplete={async () => {
                            const lFixtureDetails = await calendarService.getFixtureDetails(
                                currentFixtureId,
                                currentFixtureRoomId
                            );
                            setFixtureDetails(lFixtureDetails);
                        }}
                        hasVideo={!!fixtureDetails.mongoVideoId}
                        fixtureId={currentFixtureId}
                        userRole={userRole}
                        setShowConfirmUpload={setShowConfirmUpload}
                        closeModal={closeFixtureDetails}
                    ></Uploader>
                </Col>
            </Row>
        );

        let startRoomButton = canEdit ? (
            <Row className="mt-2">
                <Col>
                    {!currentFixtureRoomId && (
                        <Button
                            type="button"
                            variant="outline-success"
                            disabled={false}
                            className="mr-2"
                            onClick={async () => {
                                if (fixtureDetails) {
                                    try {
                                        const fixtureDetailWithRoom =
                                            await fixtureService.createFixtureRoom(currentFixtureId);
                                        const fixtureDetail =
                                            await calendarService.getFixtureDetails(
                                                currentFixtureId,
                                                fixtureDetailWithRoom.fixtureRoomId
                                            );
                                        onStartRoom && onStartRoom(fixtureDetail);
                                        setFixtureDetails(fixtureDetail);
                                    } catch (error) {
                                        toast.error(`Error creating room: ${error.message}`);
                                    }
                                }
                            }}
                        >
                            Start Room
                        </Button>
                    )}
                </Col>
            </Row>
        ) : (
            <></>
        );

        let userRows =
            fixtureDetails.assigned.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="3" className="text-center">
                        No assigned users
                    </td>
                </tr>
            ) : (
                fixtureDetails.assigned.map((x) => {
                    return (
                        <tr key={x.userId}>
                            <td>{x.name}</td>
                            <td>{x.roleName}</td>
                            <td>
                                {canEdit && (
                                    <Button
                                        variant="whiteSpace"
                                        style={{cursor: "pointer"}}
                                        onClick={async () => {
                                            await calendarService.unassignUser(
                                                currentFixtureRoomId,
                                                x.userId
                                            );
                                            const lFixtureDetails =
                                                await calendarService.getFixtureDetails(
                                                    currentFixtureId,
                                                    currentFixtureRoomId
                                                );
                                            setFixtureDetails(lFixtureDetails);
                                        }}
                                    >
                                        <i
                                            className="fas fa-fw fa-trash"
                                            style={{color: "red"}}
                                        ></i>
                                    </Button>
                                )}
                            </td>
                        </tr>
                    );
                })
            );

        let matchTeams = fixtureDetails.TBC ? (
            <></>
        ) : (
            <>
                <Row>
                    <Col md="4">Home Team Name:</Col>
                    <Col md="8">{fixtureDetails.homeTeam.name}</Col>
                </Row>
                <Row>
                    <Col md="4">Away Team Name:</Col>
                    <Col md="8">{fixtureDetails.awayTeam.name}</Col>
                </Row>
            </>
        );

        const handleUploadConfirmation = (confirmed) => {
            setShowConfirmUpload(false);

            if (confirmed) {
                if (uploaderRef.current) {
                    uploaderRef.current.startUpload();
                }
            }
        };
        return (
            <>
                {showConfirmUpload && (
                    <ConfirmVideoUploadModal
                        showConfirmUpload={showConfirmUpload}
                        handleUploadConfirmation={handleUploadConfirmation}
                    />
                )}

                <Modal
                    show={show}
                    onHide={closeFixtureDetails}
                    backdrop="static"
                    centered
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <h3>
                                {fixtureDetails.title}
                                {fixtureDetails.roomActive && (
                                    <Badge variant="success" className="ml-1">
                                        Room Active
                                    </Badge>
                                )}
                                {fixtureDetails.roomEnded && (
                                    <Badge variant="info" className="ml-1">
                                        Room Ended
                                    </Badge>
                                )}
                            </h3>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div>
                            {fixtureDetails.isForTraining && (
                                <Row>
                                    <Col>
                                        <h3>Training name: {fixtureDetails.trainingName}</h3>
                                    </Col>
                                </Row>
                            )}
                            <h4>Fixture Details</h4>
                            {matchTeams}
                            <Row>
                                <Col md="4">Level:</Col>
                                <Col md="8">{fixtureDetails.level}</Col>
                            </Row>
                            <Row>
                                <Col md="4">Date:</Col>
                                <Col md="8">{fixtureDetails.dateString}</Col>
                            </Row>
                            <Row>
                                <Col md="4">Kickoff:</Col>
                                <Col md="8">{fixtureDetails.kickoffTimeString}</Col>
                            </Row>
                            <Row>
                                <Col md="4">Season:</Col>
                                <Col md="8">{fixtureDetails.seasonName}</Col>
                            </Row>
                            <Row>
                                <Col md="4">Competition Name:</Col>
                                <Col md="8">{fixtureDetails.competitionName}</Col>
                            </Row>

                            <Row style={{wordWrap: "break-word"}}>
                                <Col md="4">Video String:</Col>
                                <Col md="8">
                                    {fixtureDetails?.mongoVideoId !== ""
                                        ? fixtureDetails.mongoVideoId
                                        : "-"}
                                </Col>
                            </Row>
                            <Row style={{wordWrap: "break-word", marginBottom: "10px"}}>
                                <Col md="4">Bureau Video Id:</Col>
                                <Col md="8">
                                    {fixtureDetails.bureauVideoId &&
                                    fixtureDetails.bureauVideoId !== ""
                                        ? fixtureDetails.bureauVideoId
                                        : "-"}
                                </Col>
                            </Row>

                            {videoUploadControls}
                            {startRoomButton}

                            {((fixtureDetails.roomActive &&
                                    userRole !== ApplicationRoles.Uploader) ||
                                (fixtureDetails.roomEnded &&
                                    userRole === ApplicationRoles.Manager)) && (
                                <Row className="pt-2">
                                    <Col>
                                        <Link to={`/scoring/${currentFixtureRoomId}`}>
                                            Room link
                                        </Link>
                                    </Col>
                                </Row>
                            )}
                            {userRole !== ApplicationRoles.Uploader &&
                                (fixtureDetails.roomActive || fixtureDetails.roomEnded) && (
                                    <Row className="pt-2">
                                        <Col>
                                            <Link
                                                to={`/scoring/${currentFixtureRoomId}/review`}
                                            >
                                                Review
                                            </Link>
                                        </Col>
                                    </Row>
                                )}
                        </div>

                        {userRole === ApplicationRoles.Manager && (
                            <div className="pt-2">
                                <h4>Assigned Users</h4>
                                {!currentFixtureRoomId && (
                                    <p className="font-weight-bold text-danger"> A room must be started before assigning
                                        a user to it.</p>
                                )}
                                {canEdit && (
                                    <Row className="pb-2">
                                        <Col md="10" xs="8">
                                            <Typeahead
                                                id="my-typeahead-id"
                                                ref={typaheadRef}
                                                onChange={(selected) => {
                                                    if (selected && selected[0])
                                                        setSelectedUser(selected[0].id);
                                                    else setSelectedUser(null);
                                                }}
                                                options={users}
                                                placeholder="Type to search users"
                                            />
                                        </Col>
                                        <Col md="2" xs="4">
                                            <Button
                                                variant="success"
                                                onClick={async () => {
                                                    if (selectedUser) {
                                                        await calendarService.assignUser(
                                                            currentFixtureRoomId,
                                                            selectedUser
                                                        );

                                                        const lFixtureDetails =
                                                            await calendarService.getFixtureDetails(
                                                                currentFixtureId,
                                                                currentFixtureRoomId
                                                            );

                                                        typaheadRef.current.clear();

                                                        setFixtureDetails(lFixtureDetails);
                                                        setSelectedUser(null);
                                                    }
                                                }}
                                                disabled={!currentFixtureRoomId}
                                            >
                                                Add
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                                <Table striped bordered hover>
                                    <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Role</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>{userRows}</tbody>
                                </Table>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={closeFixtureDetails}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    } else {
        return <></>;
    }
});

export default FixtureDetailsModal;
